/**
 * ISS-038 — Skill Versions tab.
 *
 * Lists snapshots from ``GET /admin/skills/{id}/versions`` and allows the
 * admin to roll back to a previous ``template_version`` via
 * ``POST /admin/skills/{id}/rollback``. Rollback is only allowed when the
 * skill is in ``draft`` lifecycle (enforced server-side).
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from '@/components/ui';
import {
  useAdminSkillVersions,
  useRollbackAdminSkill,
} from '@/features/skills';
import type { SkillLifecycleStatus } from '@/features/skills';
import { getErrorMessage } from '@/lib/api-client';
import { useI18n } from '@/i18n';

interface SkillVersionsTabProps {
  skillId: string;
  lifecycleStatus: SkillLifecycleStatus;
}

export function SkillVersionsTab({
  skillId,
  lifecycleStatus,
}: SkillVersionsTabProps) {
  const { t } = useI18n();
  const { data, isLoading, error } = useAdminSkillVersions(skillId);
  const rollback = useRollbackAdminSkill(skillId);
  const [pending, setPending] = useState<string | null>(null);

  const canRollback = lifecycleStatus === 'draft';

  const onRollback = async (target_version: string) => {
    if (
      !confirm(
        t('admin.skills.versions.rollbackConfirm', { targetVersion: target_version }),
      )
    )
      return;
    try {
      setPending(target_version);
      await rollback.mutateAsync({ target_version });
      toast.success(
        t('admin.skills.versions.rolledBack', { targetVersion: target_version }),
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPending(null);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6 flex items-center justify-center">
        <Spinner />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="p-6 text-center text-destructive">
        {t('admin.skills.versions.loadFailed')}
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-4">
      {!canRollback && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm">
          {t('admin.skills.versions.rollbackNotice1')}
          <code>draft</code>
          {t('admin.skills.versions.rollbackNotice2')}
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.skills.versions.version')}</TableHead>
            <TableHead>{t('admin.skills.versions.publishedAt')}</TableHead>
            <TableHead>{t('admin.skills.versions.publishedBy')}</TableHead>
            <TableHead className="text-right">
              {t('admin.skills.versions.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.length === 0 ? (
            <TableEmpty
              colSpan={4}
              message={t('admin.skills.versions.empty')}
            />
          ) : (
            data.items.map((v) => (
              <TableRow key={v.template_version}>
                <TableCell className="font-mono">{v.template_version}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(v.published_at).toLocaleString()}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {v.published_by ?? '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canRollback || pending === v.template_version}
                    onClick={() => onRollback(v.template_version)}
                  >
                    {pending === v.template_version
                      ? t('admin.skills.versions.rollingBack')
                      : t('admin.skills.versions.rollback')}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

export default SkillVersionsTab;
